import { useMutation, UseMutationResult } from '@tanstack/react-query'
import { gql } from 'graphql-request'

import { useGraphQLClient } from '@/hooks/useGraphQLClient'
import { Cart } from '@/types/cart'

import { NoteAcceptance } from '../stores/useCartStore'

import { CartItemInputType, DirectionType, ShipRouteInputType } from './useCartItemCreateApi'

const UPSERT_CART_MUTATION = gql`
  mutation cartUpsert($input: CartUpsertInput!) {
    cartUpsert(input: $input) {
      cart {
        id
        cartToken
        coupons {
          coupon {
            code
          }
          amountCents
        }
        giftCards {
          giftCard {
            displayCode
            id
          }
          amountCents
        }
        displayId
        orderSummary {
          id
          legs {
            id
            legIndex
            pickupFeeCents
            travelers {
              id
              name
              email
              phone
              shipments {
                id
                trackingId
                totalPriceCents
                retailShipRate
                paidInsuranceCents
                handlingFeeCents
                fuelSurchargeCents
                clearanceFeeCents
                upgradeDiscount
                shipmentLegName
                wholesaleDiscountCents
                shipDate
                arrivalDate
                packagingId
                additionalInfo
                products {
                  id
                  fullName
                  displayName
                  priceCents
                  type
                  coverageAmountCents
                  productLine {
                    id
                    name
                    icon
                    displayName
                  }
                }
              }
            }
            origin {
              address1
              address2
              city
              state
              zip5
              countryCode
            }
            destination {
              address1
              address2
              city
              state
              zip5
              countryCode
            }
          }
        }
      }
    }
  }
`

export interface CartUpsertItem {
  shipRoute: ShipRouteInputType
  shipDate: string // ISO8601Date is typically represented as a string in TypeScript
  arrivalDate: string
  dropOff: boolean
  direction: DirectionType
  items: CartItemInputType[]
  noteAcceptance?: NoteAcceptance[]
}

export interface UpsertCartInput {
  device?: string
  cartToken?: string
  anonymousId?: string // RudderStack anonymousId — attribution stitch key (stored on the cart)
  userId?: string
  items: CartUpsertItem[]
  noteAcceptance?: NoteAcceptance[]
  paymentMethodType?: string
  couponCode?: string
  giftCardIds?: string[]
  experimentVariationId?: string
  experimentId?: string
}

interface UpsertCartMutation {
  cartUpsert: Cart
}

export const useCartUpsertApi = (): UseMutationResult<Cart, Error, UpsertCartInput, unknown> => {
  const client = useGraphQLClient() // Avoid re-creating the client on every mutation

  const upsertCartApi = async (input: UpsertCartInput): Promise<Cart> => {
    const result = await client.request<UpsertCartMutation>(UPSERT_CART_MUTATION, { input })

    if (!result?.cartUpsert) {
      throw new Error('No cart returned from API')
    }

    return result.cartUpsert
  }

  return useMutation<Cart, Error, UpsertCartInput>({
    mutationFn: upsertCartApi,
  })
}

export default useCartUpsertApi
